/**
 * Uruchomienie przeniesienia rekordów ze starego WF — panel ITE (ADR-0112, pakiet 4).
 *
 * Okno prowadzi przez dwa kroki w ustalonej kolejności: najpierw **próba na sucho**, dopiero
 * potem przebieg na ostro. Przycisk zapisu pojawia się wyłącznie po obejrzeniu wyniku próby —
 * nie dlatego, że tak ładniej, tylko dlatego, że to operacja na tysiącach rekordów cudzej
 * kartoteki i jedyny moment, w którym widać skutki mapowania PRZED ich wywołaniem.
 *
 * Raport pokazuje podział na rodzaje wyników, a nie samą liczbę. „Przeniesiono 4580" nie mówi
 * nic; „założono 4200, zaktualizowano 300, pominięto 80 bez firmy" mówi, czy mapowanie jest
 * kompletne.
 *
 * Kartotekę wybiera się tu, a nie osobnym przyciskiem na liście baz: przebieg jest zawsze
 * dla JEDNEJ kartoteki, a reguły są dla wszystkich takie same. Zmiana kartoteki KASUJE wynik
 * poprzedniej próby — inaczej dałoby się puścić zapis pracowników na ostro, oglądając
 * wcześniej próbę kontrahentów.
 *
 * ── DWA KROKI, KTÓRE ŁATWO POMYLIĆ ──
 *
 * „Pobierz ze starej bazy" i „Przenieś" to nie to samo. Pierwsze ZLECA zadanie programowi
 * u klienta i kończy się od razu — dane przyjdą, gdy program je odczyta. Drugie działa na
 * tym, co już przyszło, i nie dotyka bazy klienta w ogóle. Rozdzielenie jest celowe: dzięki
 * niemu da się poprawić mapowanie i przenieść te same dane jeszcze raz, bez ponownego
 * obciążania produkcyjnej bazy klienta.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { CloudDownload, FlaskConical, Play } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { RadioGroup } from '@/components/RadioGroup';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

type RecordAction =
  | 'created'
  | 'updated'
  | 'unchanged'
  | 'skipped_no_company'
  | 'skipped_no_name'
  | 'failed';

interface RecordOutcome {
  sourceRecordId: string;
  action: RecordAction;
  warnings: string[];
  error?: string;
}

interface LoadReport {
  dryRun: boolean;
  setupWarning?: string;
  actorWarning?: string;
  processed: number;
  counts: Record<RecordAction, number>;
  deferredReferences: number;
  details: RecordOutcome[];
}

const KOLEJNOSC: RecordAction[] = [
  'created',
  'updated',
  'unchanged',
  'skipped_no_company',
  'skipped_no_name',
  'failed',
];

/** Kartoteki, które umie już przenieść serwer (ADR-0111, pakiety 4–5). */
const KARTOTEKI = ['contractor', 'employee'] as const;
type Kartoteka = (typeof KARTOTEKI)[number];

export interface LegacyMigrationRunModalProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  migrationId: string;
  source: { id: string; name: string } | null;
}

export function LegacyMigrationRunModal({
  open,
  onClose,
  tenantId,
  migrationId,
  source,
}: LegacyMigrationRunModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [entity, setEntity] = useState<Kartoteka>('contractor');
  const [report, setReport] = useState<LoadReport | null>(null);
  const [potwierdzenie, setPotwierdzenie] = useState(false);

  const sciezka = source
    ? `/admin/tenants/${tenantId}/legacy-migration/${migrationId}/sources/${source.id}/load/${entity}`
    : null;

  const nazwaKartoteki = t(`admin.tenants.legacyMigration.run.entity.${entity}`);

  const sciezkaZadan = source
    ? `/admin/tenants/${tenantId}/legacy-migration/${migrationId}/sources/${source.id}/jobs`
    : null;

  const uruchom = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const res = await api.post<LoadReport>(sciezka!, { dryRun });
      return res.data;
    },
    onSuccess: (res) => {
      setReport(res);
      setPotwierdzenie(false);
      if (!res.dryRun) {
        toast.show({
          variant: 'success',
          title: t('admin.tenants.legacyMigration.run.done', {
            created: res.counts.created,
            updated: res.counts.updated,
          }),
        });
      }
    },
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t(
          `admin.tenants.legacyMigration.errors.${extractErrorCode(err)}`,
          t('admin.tenants.legacyMigration.run.failed'),
        ),
      }),
  });

  const pobierz = useMutation({
    mutationFn: async () => {
      await api.post(sciezkaZadan!, {
        jobType: entity === 'employee' ? 'read_employees' : 'read_contractors',
      });
    },
    onSuccess: () =>
      toast.show({
        variant: 'success',
        title: t('admin.tenants.legacyMigration.toast.jobQueued'),
      }),
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t(
          `admin.tenants.legacyMigration.errors.${extractErrorCode(err)}`,
          t('admin.tenants.legacyMigration.toast.jobFailed'),
        ),
      }),
  });

  const zamknij = () => {
    setReport(null);
    setPotwierdzenie(false);
    onClose();
  };

  // Wynik próby dotyczy KONKRETNEJ kartoteki — po przełączeniu przestaje cokolwiek znaczyć,
  // więc znika razem z przyciskiem zapisu na ostro.
  const zmienKartoteke = (nowa: Kartoteka) => {
    setEntity(nowa);
    setReport(null);
    setPotwierdzenie(false);
  };

  const nieudane = report?.counts.failed ?? 0;

  return (
    <>
      <Modal
        open={open}
        onClose={zamknij}
        size="lg"
        // Przeniesienie kilku tysięcy rekordów trwa. Zamknięcie okna kliknięciem w tło
        // w jego trakcie zostawiłoby wdrożeniowca bez raportu z operacji, która nadal biegnie.
        closeOnBackdrop={!uruchom.isPending}
        closeOnEsc={!uruchom.isPending}
        title={t('admin.tenants.legacyMigration.run.title', { name: source?.name ?? '' })}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--text-secondary)]">
            {t('admin.tenants.legacyMigration.run.intro')}
          </p>
          <p className="text-sm text-[var(--text-secondary)]">
            {t('admin.tenants.legacyMigration.run.fetchHint')}
          </p>

          <RadioGroup<Kartoteka>
            name="legacy-run-entity"
            legend={t('admin.tenants.legacyMigration.run.entityLegend')}
            value={entity}
            onChange={zmienKartoteke}
            options={KARTOTEKI.map((k) => ({
              value: k,
              label: t(`admin.tenants.legacyMigration.run.entity.${k}`),
            }))}
            data-testid="legacy-run-entity"
          />

          {report?.setupWarning && <Alert tone="warning">{report.setupWarning}</Alert>}
          {report?.actorWarning && <Alert tone="info">{report.actorWarning}</Alert>}

          {report && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">
                {report.dryRun
                  ? t('admin.tenants.legacyMigration.run.dryResult', { count: report.processed })
                  : t('admin.tenants.legacyMigration.run.liveResult', { count: report.processed })}
              </span>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {KOLEJNOSC.filter((a) => report.counts[a] > 0).map((a) => (
                  <div key={a} className="flex justify-between gap-4">
                    <dt className="text-[var(--text-secondary)]">
                      {t(`admin.tenants.legacyMigration.run.action.${a}`)}
                    </dt>
                    <dd className="font-medium">{report.counts[a]}</dd>
                  </div>
                ))}
              </dl>

              {report.deferredReferences > 0 && (
                <Alert tone="info">
                  {t('admin.tenants.legacyMigration.run.deferred', {
                    count: report.deferredReferences,
                  })}
                </Alert>
              )}

              {nieudane > 0 && (
                <Alert tone="error" title={t('admin.tenants.legacyMigration.run.failedTitle')}>
                  <ul className="list-disc pl-4">
                    {report.details
                      .filter((d) => d.action === 'failed')
                      .slice(0, 10)
                      .map((d) => (
                        <li key={d.sourceRecordId}>
                          {d.sourceRecordId}: {d.error}
                        </li>
                      ))}
                  </ul>
                </Alert>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={zamknij}>
              {t('common.close')}
            </Button>
            <Button
              variant="secondary"
              leftIcon={<CloudDownload className="size-4" />}
              onClick={() => pobierz.mutate()}
              disabled={pobierz.isPending || uruchom.isPending}
            >
              {t('admin.tenants.legacyMigration.run.fetch')}
            </Button>
            <Button
              variant="secondary"
              leftIcon={<FlaskConical className="size-4" />}
              onClick={() => uruchom.mutate(true)}
              disabled={uruchom.isPending}
            >
              {t('admin.tenants.legacyMigration.run.dryRun')}
            </Button>
            {/* Zapis na ostro dopiero po obejrzeniu próby — kolejność jest celowa. */}
            {report?.dryRun && (
              <Button
                leftIcon={<Play className="size-4" />}
                onClick={() => setPotwierdzenie(true)}
                disabled={uruchom.isPending}
              >
                {t('admin.tenants.legacyMigration.run.live')}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={potwierdzenie}
        title={t('admin.tenants.legacyMigration.run.confirm.title')}
        message={t('admin.tenants.legacyMigration.run.confirm.message', {
          created: report?.counts.created ?? 0,
          updated: report?.counts.updated ?? 0,
          entity: nazwaKartoteki,
        })}
        confirmLabel={t('admin.tenants.legacyMigration.run.confirm.ok')}
        variant="primary"
        confirming={uruchom.isPending}
        onConfirm={() => uruchom.mutate(false)}
        onClose={() => setPotwierdzenie(false)}
      />
    </>
  );
}
